import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import * as OnboardingActions from './onboarding.actions';
import * as OnboardingSelectors from './onboarding.selectors';
import { IOnboardingState } from './onboarding.reducers';

import { IOnboardingSubmitResponse } from './onboarding.responses.dto';
import { HttpErrorResponse } from '@angular/common/http';
import { IOnboardingFormData } from './onboarding.dto';

@Injectable({ providedIn: 'root' })
export class OnboardingFacade {
  loading$: Observable<boolean> = this.store.select(OnboardingSelectors.selectOnboardingLoading);
  error$: Observable<HttpErrorResponse | string | null> = this.store.select(OnboardingSelectors.selectOnboardingError);
  success$: Observable<boolean> = this.store.select(OnboardingSelectors.selectOnboardingSuccess);
  onboardingResponse$: Observable<IOnboardingSubmitResponse | null> = this.store.select(OnboardingSelectors.selectOnboardingResponse);

  // User Profile Selectors
  userProfileFirstName$: Observable<string> = this.store.select(OnboardingSelectors.selectUserProfileFirstName);
  userProfileLastName$: Observable<string> = this.store.select(OnboardingSelectors.selectUserProfileLastName);
  userProfilePassword$: Observable<string | undefined> = this.store.select(OnboardingSelectors.selectUserProfilePassword);
  userProfileJobTitle$: Observable<string> = this.store.select(OnboardingSelectors.selectUserProfileJobTitle);
  userProfileCompanyName$: Observable<string | undefined> = this.store.select(OnboardingSelectors.selectUserProfileCompanyName);
  userProfilePhoneNumber$: Observable<string> = this.store.select(OnboardingSelectors.selectUserProfilePhoneNumber);
  userProfileTerms$: Observable<boolean> = this.store.select(OnboardingSelectors.selectUserProfileTerms);
  userProfileNoMarketing$: Observable<boolean> = this.store.select(OnboardingSelectors.selectUserProfileNoMarketing);
  userProfileImagePreview$: Observable<string | null> = this.store.select(OnboardingSelectors.selectUserProfileImagePreview);
  profileImageFile$: Observable<File | null> = this.store.select(OnboardingSelectors.selectProfileImageFile);
  userProfilePasswordTextType$: Observable<boolean> = this.store.select(OnboardingSelectors.selectUserProfilePasswordTextType);
  userProfileSubmitted$: Observable<boolean> = this.store.select(OnboardingSelectors.selectUserProfileSubmitted);

  // Tell Us About You Selectors
  tellUsAboutYouWorkType$: Observable<string> = this.store.select(OnboardingSelectors.selectTellUsAboutYouWorkType);

  // NEW: How Plan To Use Selectors
  howPlanToUsePlanningToUse$: Observable<string> = this.store.select(OnboardingSelectors.selectHowPlanToUsePlanningToUse);

  onboardingType$: Observable<OnboardingActions.OnboardingType> = this.store.select(OnboardingSelectors.selectOnboardingType);
  onboardingFormData$: Observable<any> = this.store.select(OnboardingSelectors.selectOnboardingFormData);
  isValid$: Observable<boolean> = this.store.select(OnboardingSelectors.selectIsOnboardingDataValid);
  profileStepSkipped$: Observable<boolean> = this.store.select(OnboardingSelectors.selectProfileStepSkipped);

  constructor(private store: Store<IOnboardingState>) { }

  setOnboardingType(onboardingType: OnboardingActions.OnboardingType) {
    this.store.dispatch(OnboardingActions.setOnboardingType({ onboardingType }));
  }

  setProfileStepSkipped(skipped: boolean) {
    this.store.dispatch(OnboardingActions.setProfileStepSkipped({ skipped }));
  }

  submitOnboarding(formData: IOnboardingFormData, token: string) {
    this.store.dispatch(OnboardingActions.onboardingSubmit({ formData, token }));
  }

  submitInvitedOnboarding(formData: IOnboardingFormData, apiUrl: string) {
    this.store.dispatch(OnboardingActions.onboardingInvitedSubmit({ formData, apiUrl }));
  }

  resetState() {
    this.store.dispatch(OnboardingActions.resetOnboardingState());
  }

  // User Profile Action Dispatchers
  updateUserProfileField(field: string, value: any) {
    this.store.dispatch(OnboardingActions.updateUserProfileField({ field, value }));
  }

  updateProfileImage(profileImage: string | null) {
    this.store.dispatch(OnboardingActions.updateProfileImage({ profileImage }));
  }

  setProfileImageFile(profileImageFile: File | null) {
    this.store.dispatch(OnboardingActions.setProfileImageFile({ profileImageFile }));
  }

  togglePasswordTextType() {
    this.store.dispatch(OnboardingActions.togglePasswordTextType());
  }

  setUserProfileFormSubmitted(submitted: boolean) {
    this.store.dispatch(OnboardingActions.setUserProfileFormSubmitted({ submitted }));
  }

  // Tell Us About You Action Dispatchers
  updateTellUsAboutYouWorkType(workType: string) {
    this.store.dispatch(OnboardingActions.updateTellUsAboutYouWorkType({ workType }));
  }

  // NEW: How Plan To Use Action Dispatchers
  updateHowPlanToUsePlanningToUse(planningToUse: string) {
    this.store.dispatch(OnboardingActions.updateHowPlanToUsePlanningToUse({ planningToUse }));
  }

  checkToken(token: string) {
    this.store.dispatch(OnboardingActions.checkToken({ token }));
  }

  updateError(error: string) {
    this.store.dispatch(OnboardingActions.updateError({ error }));
  }

  teamInvitationCompleted() {
    this.store.dispatch(OnboardingActions.onboardingSubmitSucceeded({ response: {} as IOnboardingSubmitResponse }));
  }
}
